import { useState } from 'react'
import { Mail, Send } from 'lucide-react'
import type { ColumnDef } from '@tanstack/react-table'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { DataTable } from '@/components/DataTable'
import { Modal } from '@/components/Modal'
import { Toolbar } from '@/components/Toolbar'
import { FormField } from '@/components/FormField'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/useToast'
import { useAuth } from '@/auth/useAuth'
import { ApiError } from '@/lib/api'
import { formatDateTime } from '@/lib/locale'
import {
  useCreateStaffInvitation,
  useRevokeStaffInvitation,
  useStaffInvitations,
  useStaffRoles,
} from '@/features/platformAdmin/queries'
import type { StaffInvitation } from '@/features/platformAdmin/types'

const fmtDate = (v: string | null) => (v ? formatDateTime(v) : '-')

const errMsg = (err: unknown, fallback = 'Action failed.') => {
  if (err instanceof ApiError && err.body && typeof err.body === 'object' && 'error' in err.body) {
    const m = (err.body as { error: unknown }).error
    if (typeof m === 'string' && m) return m
  }
  if (err instanceof Error && err.message) return err.message
  return fallback
}

const statusVariant = (s: string): 'success' | 'warning' | 'danger' | 'secondary' =>
  s === 'accepted' ? 'success' : s === 'pending' ? 'warning' : s === 'revoked' ? 'danger' : 'secondary'

export function StaffInvitationsTab() {
  const { hasPermission } = useAuth()
  const { toast } = useToast()
  const invitationsQ = useStaffInvitations()
  const rolesQ = useStaffRoles()
  const create = useCreateStaffInvitation()
  const revoke = useRevokeStaffInvitation()

  const canInvite = hasPermission('admin.staff.invite')
  const [open, setOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [roleId, setRoleId] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [acceptLink, setAcceptLink] = useState<string | null>(null)
  const [emailSent, setEmailSent] = useState(false)
  const [confirm, setConfirm] = useState<StaffInvitation | null>(null)

  const onSubmit = async () => {
    setError(null)
    if (!email.trim() || !roleId) {
      setError('Email and role are required.')
      return
    }
    try {
      const res = await create.mutateAsync({ email: email.trim(), roleId: Number(roleId) })
      setAcceptLink(res.acceptLink)
      setEmailSent(res.emailSent)
      toast({ title: 'Invitation sent.' })
    } catch (err) {
      setError(errMsg(err))
    }
  }

  const resetModal = () => {
    setEmail('')
    setRoleId('')
    setError(null)
    setAcceptLink(null)
    setEmailSent(false)
  }

  const onRevoke = async (i: StaffInvitation) => {
    try {
      await revoke.mutateAsync(i.id)
      toast({ title: 'Invitation revoked.' })
    } catch (err) {
      toast({ variant: 'destructive', title: 'Failed', description: errMsg(err) })
    }
  }

  const invitations = invitationsQ.data?.invitations ?? []
  const roles = rolesQ.data?.roles ?? []

  const columns: ColumnDef<StaffInvitation>[] = [
    {
      accessorKey: 'email',
      header: 'Email',
      cell: ({ row }) => <span className="font-semibold">{row.original.email}</span>,
    },
    { accessorKey: 'role_name', header: 'Role', cell: ({ row }) => <Badge variant="secondary">{row.original.role_name}</Badge> },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => <Badge variant={statusVariant(row.original.status)}>{row.original.status}</Badge>,
    },
    {
      accessorKey: 'expires_at',
      header: 'Expires',
      cell: ({ row }) => (
        <span className="text-xs text-muted-foreground">{fmtDate(row.original.expires_at)}</span>
      ),
    },
    {
      accessorKey: 'invited_by_email',
      header: 'Invited by',
      cell: ({ row }) => row.original.invited_by_email ?? '-',
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) =>
        row.original.status === 'pending' ? (
          <Button size="sm" variant="danger" onClick={() => setConfirm(row.original)}>
            Revoke
          </Button>
        ) : null,
    },
  ]

  return (
    <div className="space-y-3">
      <Toolbar className="justify-end">
        {canInvite ? (
          <Button onClick={() => { resetModal(); setOpen(true) }}>
            <Send className="size-4" />
            Invite staff
          </Button>
        ) : null}
      </Toolbar>
      <Card>
        <CardContent className="p-0">
          <DataTable
            columns={columns}
            rows={invitations}
            loading={invitationsQ.isLoading}
            empty="No staff invitations."
          />
        </CardContent>
      </Card>

      <Modal
        open={open}
        onOpenChange={(v) => { setOpen(v); if (!v) resetModal() }}
        title="Invite staff member"
        footer={
          acceptLink ? (
            <>
              <Button variant="secondary" onClick={() => { resetModal() }}>
                Send another
              </Button>
              <Button onClick={() => setOpen(false)}>Done</Button>
            </>
          ) : (
            <>
              <Button variant="secondary" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button onClick={onSubmit} disabled={create.isPending}>
                Send invitation
              </Button>
            </>
          )
        }
      >
        {acceptLink ? (
          <div className="space-y-3">
            <Alert>
              <Mail className="size-4" />
              <AlertDescription>
                Invitation created. {emailSent ? 'Email sent successfully.' : 'Email send failed - share the link manually below.'}
              </AlertDescription>
            </Alert>
            <div className="break-all rounded-md bg-muted p-2 font-mono text-xs">{acceptLink}</div>
          </div>
        ) : (
          <div className="space-y-3">
            <FormField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
            />
            <FormField
              label="Role"
              control={
                <Select value={roleId} onValueChange={setRoleId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select…" />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map((r) => (
                      <SelectItem key={r.id} value={String(r.id)}>
                        {r.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              }
            />
            {error ? (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : null}
          </div>
        )}
      </Modal>

      <Modal.Confirm
        open={!!confirm}
        onOpenChange={(v) => !v && setConfirm(null)}
        title="Revoke invitation"
        body={`Revoke the pending invitation for ${confirm?.email ?? ''}?`}
        confirmLabel="Revoke"
        confirmVariant="danger"
        onConfirm={async () => {
          const c = confirm
          setConfirm(null)
          if (c) await onRevoke(c)
        }}
      />
    </div>
  )
}
